import { QueryCache, QueryClient } from '@tanstack/react-query';

import { NetworkError } from '@/errors/NetworkError';
import Logger from '@/lib/logger';
import { publishPermissionDenied } from '@/utils/requestFeedback';

const MAX_TRANSIENT_RETRIES = 2;

export function shouldRetryQuery(failureCount: number, error: Error): boolean {
  if (error.name === 'AbortError') return false;
  if (error.name === 'TimeoutError') return failureCount < 1;

  if (error instanceof NetworkError) {
    const isRetryableStatus =
      error.statusCode === 429 || error.statusCode >= 500;

    return isRetryableStatus && failureCount < MAX_TRANSIENT_RETRIES;
  }

  Logger.debug(
    `Query failed with error: ${error.message}, failureCount: ${failureCount}`
  );

  return failureCount < MAX_TRANSIENT_RETRIES;
}

export function handleTerminalQueryError(error: Error): void {
  if (error instanceof NetworkError && error.statusCode === 403) {
    publishPermissionDenied();
  }
}

export const queryCache = new QueryCache({
  onError: handleTerminalQueryError,
});

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: shouldRetryQuery,
    },
  },
  queryCache,
});
